import React, { useState } from 'react';
import { 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  ExclamationCircleIcon,
  CheckCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface Alert {
  id: string;
  timestamp: string;
  equipment: string;
  type: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'active' | 'acknowledged' | 'resolved';
}

interface AlertsTableProps {
  alerts: Alert[];
  onAcknowledge?: (alertId: string) => void;
  onViewDetails?: (alertId: string) => void;
}

const AlertsTable: React.FC<AlertsTableProps> = React.memo(({ alerts, onAcknowledge, onViewDetails }) => {
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'timestamp' | 'severity'>('timestamp');

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ExclamationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
      case 'info':
        return <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border border-red-200 dark:border-red-700/50">
            <div className="w-2 h-2 bg-red-600 dark:bg-red-400 rounded-full animate-pulse" />
            🔴 Active
          </span>
        );
      case 'acknowledged':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border border-amber-200 dark:border-amber-700/50">
            <div className="w-2 h-2 bg-amber-600 dark:bg-amber-400 rounded-full" />
            🟡 Acknowledged
          </span>
        );
      case 'resolved':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-700/50">
            <CheckCircleIcon className="w-3 h-3" />
            ✅ Resolved
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
            ❓ Unknown
          </span>
        );
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.severity === filter || alert.status === filter;
  });

  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (sortBy === 'timestamp') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    }
    const severityOrder = { critical: 3, warning: 2, info: 1 };
    return severityOrder[b.severity] - severityOrder[a.severity];
  });

  const handleAcknowledge = (alertId: string) => {
    if (onAcknowledge) {
      onAcknowledge(alertId);
    }
  };

  const handleViewDetails = (alertId: string) => {
    if (onViewDetails) {
      onViewDetails(alertId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Filters & Stats */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-3 pr-10 rounded-xl border-2 border-amber-200 dark:border-amber-700/50 bg-white dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 text-sm font-medium min-w-[160px] focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 appearance-none cursor-pointer"
            >
              <option value="all">All Alerts ({alerts.length})</option>
              <option value="critical">🔴 Critical ({alerts.filter(a => a.severity === 'critical').length})</option>
              <option value="warning">🟡 Warning ({alerts.filter(a => a.severity === 'warning').length})</option>
              <option value="info">🔵 Info ({alerts.filter(a => a.severity === 'info').length})</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'timestamp' | 'severity')}
              className="px-4 py-3 pr-10 rounded-xl border-2 border-amber-200 dark:border-amber-700/50 bg-white dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 text-sm font-medium min-w-[140px] focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 appearance-none cursor-pointer"
            >
              <option value="timestamp">⏰ Latest First</option>
              <option value="severity">⚡ By Severity</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-700 dark:text-red-300 font-medium">{alerts.filter(a => a.status === 'active').length} Active</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="text-emerald-700 dark:text-emerald-300 font-medium">{alerts.filter(a => a.status === 'resolved').length} Resolved</span>
          </div>
        </div>
      </div>

      {/* Enhanced Modern Table */}
      <div className="overflow-hidden rounded-2xl border-2 border-amber-200 dark:border-amber-700/50 shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50">
              <tr>
                <th className="px-6 py-5 text-left text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  ⚡ Severity
                </th>
                <th className="hidden sm:table-cell px-6 py-5 text-left text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  🕰️ Time
                </th>
                <th className="px-6 py-5 text-left text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  💻 Equipment
                </th>
                <th className="hidden md:table-cell px-6 py-5 text-left text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  📊 Alert Type
                </th>
                <th className="px-6 py-5 text-left text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  🟢 Status
                </th>
                <th className="px-6 py-5 text-right text-xs font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                  ⚙️ Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-amber-950/10 divide-y divide-amber-200 dark:divide-amber-700/30">
              {sortedAlerts.map((alert, index) => (
                <tr 
                  key={alert.id} 
                  className="hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all duration-300 animate-fade-in-up border-l-4 border-transparent hover:border-amber-400 dark:hover:border-amber-500"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl border-2 ${
                        alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/50' :
                        alert.severity === 'warning' ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/50' :
                        'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/50'
                      }`}>
                        {getSeverityIcon(alert.severity)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                          {alert.severity}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                          {formatTimestamp(alert.timestamp)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white font-medium">
                      {formatTimestamp(alert.timestamp)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(alert.timestamp).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {alert.equipment}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 md:hidden">
                      {alert.type}
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {alert.type}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(alert.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {alert.status === 'active' && (
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="btn btn-sm btn-success"
                          title="Acknowledge Alert"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          <span className="hidden sm:inline">Acknowledge</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewDetails(alert.id)}
                        className="btn btn-sm btn-secondary"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">Details</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sortedAlerts.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center">
            <InformationCircleIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No alerts found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {filter === 'all' 
              ? 'No alerts to display. Your system is running smoothly!' 
              : `No ${filter} alerts found. Try adjusting your filter.`
            }
          </p>
        </div>
      )}
    </div>
  );
});

export default AlertsTable;